import * as d3 from "d3";
import { BLOCK, CLICK, DIV, DRAG, DRAGEND, DRAGSTART, MOUSEENTER, MOUSELEAVE, NONE, P, PX } from "../../library/constants";
import { getExcelTable } from "@zebrabi/data-helpers/excelDataHelpers";
import { getMaxRoleCount } from "@zebrabi/data-helpers/tablesMetadata";
import { ToolbarOptions } from "@zebrabi/global-toolbar-old/interface/ToolbarOption";
import { Visual } from "../../visual";
import { VarianceSettings } from "../../settings/varianceSettings";
import { ViewModel } from "../../settings/viewModel";
import { getOfficeSettings } from "@zebrabi/office-settings";
import { DATA_SOURCE, DataSource, createDialog } from "@zebrabi/data-helpers/editData";
import { MeasureRoles, CategoricalRoles } from "@zebrabi/data-helpers/fieldAssignment";
import { getColumnRole } from "@zebrabi/data-helpers/visualHelpers";
import { DataView, DataViewMetadataColumn } from "@zebrabi/matrix-data";
import BaseSwitcherWithHeaderOld from "./BaseSwitcherWithHeaderOld";
import { ColumnOptions } from "../../definitions";
import { isPlanScenario, isForecastScenario } from "../../helpers";

class FieldsTablesSwitcher extends BaseSwitcherWithHeaderOld {
    static readonly CLASS_NAME = `FieldsTablesSwitcher`;

    toolbarOptions: ToolbarOptions = {
        collapsed: true,
        elementName: "Data fields",
        icon: "fields-icon-base64",
        type: "button",
        actions: [],
    };

    // Value buckets
    public bucketActual: d3.Selection<HTMLDivElement, any, any, any>;
    public bucketPY: d3.Selection<HTMLDivElement, any, any, any>;
    public bucketPL: d3.Selection<HTMLDivElement, any, any, any>;
    public bucketFC: d3.Selection<HTMLDivElement, any, any, any>;
    public bucketComment: d3.Selection<HTMLDivElement, any, any, any>;

    public bucketCategory: d3.Selection<HTMLDivElement, any, any, any>;
    public bucketGroup: d3.Selection<HTMLDivElement, any, any, any>;

    /** Drag name of the fictional "Columns (n)" field, so a drop on Group replaces the group column */
    private static readonly COLUMNS_FIELD = "\u0000columns";
    /** Flat source (Excel range / table, PowerPoint data editor): the Fields list shows the source columns */
    private columnFieldsEnabled = false;
    /** Slot the insertion marker points at during a same-bucket drag; null when dropping would not reorder */
    private pendingInsertIndex: number = null;
    private innerForm: d3.Selection<HTMLDivElement, any, any, any>;
    /** Translucent copy of the dragged item that follows the cursor */
    private dragGhost: d3.Selection<HTMLDivElement, any, any, any> = null;
    /** Cursor position inside the item at drag start, relative to its top-left corner */
    private dragGrabOffset = { x: 0, y: 0 };
    private fieldsList: d3.Selection<HTMLDivElement, any, any, any> = null;
    /** Whether the item being dragged may be dropped on the Fields list to remove it from the pivot */
    private canDropOnFieldsList = false;

    constructor(
        private settings: VarianceSettings,
        private dataView: DataView,
        private viewModel: ViewModel
    ) {
        super();
    }

    /**
     * Transpiling will lose the type of the class once minimised, method is used the type is preserved
     */
    public getClassName(): string {
        return FieldsTablesSwitcher.CLASS_NAME;
    }

    buttonAction(action: string, message: string): void {}

    update(message: Map<string, any>): void {
        //todo: check why this method is empty on charts
        if (message.has("viewer")) {
            if (message.get("viewer") == "true") {
                this.switcherContainer.style.display = NONE;
            } else {
                this.switcherContainer.style.display = BLOCK;
            }
        }

        if (message.has("settings")) {
            this.settings = message.get("settings");
        }

        if (message.has("viewModel")) {
            this.viewModel = message.get("viewModel");
        }

        if (message.has("dataView")) {
            this.dataView = message.get("dataView");
        }

        return;
    }

    createMenuItems(switcherMenuContainer: HTMLElement, active: string = "") {
        super.createMenuItems(switcherMenuContainer, active);
        const body = switcherMenuContainer.querySelector(".body");
        const fieldsForm = d3.select(body).append(DIV).classed("fields-settings-form-2", true);
        let innerForm = fieldsForm.append(DIV).classed("inner-form", true);
        this.createFieldsForm(innerForm);
    }

    private drawValueFieldsItems(innerForm: d3.Selection<HTMLDivElement, any, any, any>) {
        this.dataView?.metadata?.columns.forEach((dataColumn) => {
            const currentRole = getColumnRole(dataColumn);

            let item = innerForm.append(DIV).classed("item-vf", true);
            item.append("span").classed("label", true).text(dataColumn.displayName);

            // use position from scenario options to set PL/FC columns order
            const scenarioOptions = Visual.getInstance().viewModel?.scenarioOptions;
            if (scenarioOptions) {
                const scenarioOptionKey = Object.keys(scenarioOptions).find(
                    (key) => scenarioOptions[key].fieldName === dataColumn.displayName && scenarioOptions[key].index === dataColumn.index
                );
                const scenarioOption: ColumnOptions = scenarioOptionKey ? scenarioOptions[scenarioOptionKey] : null;
                if (scenarioOption && (dataColumn.roles[MeasureRoles.Plan] || dataColumn.roles[MeasureRoles.Forecast])) {
                    item.style("order", scenarioOption.position ?? 0); //scenarioOption.position);
                }
            }

            const bucket = this.getBucketForColumnRole(dataColumn);
            if (!currentRole || !bucket) {
                return;
            }
            bucket.node().append(item.node());
            item.data([dataColumn]);

            if (this.columnFieldsEnabled && this.dataView?.columnsAsGroups) {
                // cross table: "Columns (n)" sits in Group; the value item stands for all the group columns
                if (dataColumn.roles[CategoricalRoles.Group]) {
                    item.select(".label").text(this.columnsLabel());
                    this.renderColumnsItem(<d3.Selection<HTMLDivElement, unknown, HTMLElement, unknown>>item);
                } else if (dataColumn.index !== undefined && !dataColumn.roles[MeasureRoles.Comments]) {
                    item.attr("title", "The values of the columns used as groups; tick columns in the list to change them");
                }
                return;
            }
            if (this.columnFieldsEnabled && dataColumn.roles[CategoricalRoles.Group] && this.getGroupBySourceColumn()) {
                this.renderGroupByItem(<d3.Selection<HTMLDivElement, unknown, HTMLElement, unknown>>item);
                return;
            }

            //to-check: index for categorical roles
            if (dataColumn.index === undefined && currentRole in MeasureRoles) {
                return;
            }

            const dropTargets = this.getDropTargets(currentRole, dataColumn);
            const canRemove = this.canRemoveBucketField(dataColumn, currentRole);
            const removable = this.columnFieldsEnabled && (currentRole in MeasureRoles || currentRole === CategoricalRoles.Category);
            if (removable && this.isLastFieldInArea(currentRole)) {
                item.attr("title", this.getLockedFieldHint(currentRole));
            } else if (canRemove) {
                item.append("button")
                    .classed("remove-field", true)
                    .attr("title", "Remove field")
                    .text("×")
                    .on(CLICK, () => {
                        d3.event.stopPropagation();
                        this.removeBucketField(dataColumn);
                    });
            }

            let draggable = d3
                .drag()
                .filter(() => !(<Element>d3.event.target).closest(".remove-field"))
                .on(DRAGSTART, () => this.liftDraggedItem(item, dropTargets, canRemove))
                .on(DRAG, () => {
                    this.followCursor();
                    this.highlightBucketUnderCursor(dropTargets);
                    if (dropTargets.includes(CategoricalRoles.Category)) {
                        this.updateInsertionMarker(this.bucketCategory, item);
                    }
                })
                .on(DRAGEND, () => {
                    const returnData = new Map<string, any>();
                    // read before dropDraggedItem clears them
                    const targetIndex = this.pendingInsertIndex;
                    const droppedOnFieldsList = this.canDropOnFieldsList && this.isCursorOverFieldsList();
                    this.dropDraggedItem(item);

                    if (droppedOnFieldsList) {
                        this.removeBucketField(dataColumn);
                        return;
                    }

                    let newRole = this.findRoleUnderCursor(d3.event);
                    if (newRole && !dropTargets.includes(newRole)) {
                        newRole = null;
                    }
                    if (this.columnFieldsEnabled && newRole === CategoricalRoles.Group && this.isTextSourceColumn(dataColumn)) {
                        // a text column dropped on Group: the rows are pivoted by its values
                        this.placeInBucket(item, dataColumn.displayName, newRole);
                        void this.setGroupByColumn(dataColumn.displayName);
                        return;
                    }
                    if (this.columnFieldsEnabled && newRole === CategoricalRoles.Category) {
                        if (currentRole === CategoricalRoles.Category) {
                            if (targetIndex !== null) {
                                this.placeInBucket(item, dataColumn.displayName, newRole, targetIndex);
                                void this.moveCategoryLevel(dataColumn.displayName, targetIndex);
                            }
                        } else if (this.isTextSourceColumn(dataColumn)) {
                            // a text column (Comments, text measure) dropped on Category becomes the last level
                            this.placeInBucket(item, dataColumn.displayName, newRole);
                            void this.setCategoryColumns([...this.getCategoryLevels(), dataColumn.displayName]);
                        }
                        return;
                    }
                    if (this.columnFieldsEnabled && currentRole === CategoricalRoles.Category) {
                        // a level dropped on Comments or Actual leaves Category and becomes that measure
                        if (newRole) {
                            this.placeInBucket(item, dataColumn.displayName, newRole);
                        }
                        void this.moveTextColumn(dataColumn.displayName, newRole, dropTargets);
                        return;
                    }
                    if (!newRole || newRole in CategoricalRoles || currentRole in CategoricalRoles) {
                        // without a pivot source, categorical roles can't change
                        newRole = currentRole;
                    } else {
                        const newBucket = this.getBucketForRole(newRole);
                        newBucket.node().append(item.node());
                    }

                    // handle reordering of PL/FC columns within buckets, use position from scenario options
                    if (newRole === currentRole && (newRole === MeasureRoles.Plan || newRole === MeasureRoles.Forecast)) {
                        const scenarioOptions = Visual.getInstance().viewModel?.scenarioOptions;
                        let columnOption: ColumnOptions = null;
                        if (scenarioOptions) {
                            const scenarioOptionKey = Object.keys(scenarioOptions).find(
                                (key) =>
                                    scenarioOptions[key].fieldName === dataColumn.displayName
                                    && scenarioOptions[key].index === dataColumn.index
                            );
                            const scenarioOption: ColumnOptions = scenarioOptionKey ? scenarioOptions[scenarioOptionKey] : null;
                            if (scenarioOption && (dataColumn.roles[MeasureRoles.Plan] || dataColumn.roles[MeasureRoles.Forecast])) {
                                columnOption = scenarioOption;
                            }
                        }

                        if (columnOption) {
                            const topPosition = Number(item.style("top").replace("px", ""));
                            //get measure role position from topPosition
                            const newRolePosition = this.getPositionWithinBucket(topPosition, newRole);
                            if (columnOption.position !== newRolePosition) {
                                // Reorder items in bucket
                                const scenarioOptionRefKey = Object.keys(scenarioOptions).find((key) => {
                                    const column = <ColumnOptions>scenarioOptions[key];
                                    return (
                                        column?.position === newRolePosition
                                        && ((currentRole === MeasureRoles.Plan && isPlanScenario(column.scenario))
                                            || (currentRole === MeasureRoles.Forecast && isForecastScenario(column.scenario)))
                                    );
                                });

                                if (scenarioOptionRefKey) {
                                    const scenarioOptionRef = scenarioOptions[scenarioOptionRefKey];
                                    scenarioOptionRef.position = columnOption.position;
                                    columnOption.position = newRolePosition;
                                }
                            }
                        }
                    } else if (
                        newRole === MeasureRoles.Plan
                        || newRole === MeasureRoles.Forecast
                        || currentRole === MeasureRoles.Plan
                        || currentRole === MeasureRoles.Forecast
                    ) {
                        Visual.getInstance().resetPLFCReorder = true;
                    }

                    if (newRole in MeasureRoles && currentRole in MeasureRoles) {
                        this.applyMeasureRole(dataColumn, <MeasureRoles>currentRole, <MeasureRoles>newRole, returnData);
                    }

                    void this.dropFade().then(() => this.notify(returnData, `${this.getClassName()}Observer`));
                });

            // a field with no bucket to go to (Comments) is still draggable back to the Fields list to remove it
            if (dropTargets.length > 0 || canRemove) {
                item.classed("draggable", true);
                draggable(item);
            }
        });
    }

    /**
     * Flat source (Excel range / table, PowerPoint data editor): lists every column of the source.
     * Checkbox: on a measure = used in / left out of the visual; on a level = in / out of Category; on a text column =
     * the group column or not. Drag: measures onto the value buckets (a role switch), text columns onto Category,
     * Group or Comments.
     */
    private drawColumnFieldsList(innerForm: d3.Selection<HTMLDivElement, any, any, any>) {
        const source = this.dataView?.sourceValueColumns;
        if (!this.columnFieldsEnabled || !source?.length) {
            return;
        }
        const columns = this.dataView.metadata.columns.filter((c) => c.index !== undefined);
        const measures = source.filter((s) => !s.isCategory && !s.isComment && !s.isText);
        const measuresInUse = measures.filter((s) => !s.excluded).length;
        const levels = this.getCategoryLevels();

        innerForm.append(P).text("Fields");
        const list = innerForm.append(DIV).classed("fields-list", true);
        this.fieldsList = list;

        // the measures are listed together under "Columns (n)", as its members; the text columns keep their sheet order
        // around that block (those before the first measure above it, the rest below)
        const firstMeasure = measures[0] ?? null;
        const firstMeasureAt = firstMeasure ? source.indexOf(firstMeasure) : source.length;
        const ordered = [
            ...source.slice(0, firstMeasureAt),
            ...measures,
            ...source.slice(firstMeasureAt).filter((s) => !measures.includes(s)),
        ];

        ordered.forEach((field) => {
            if (field === firstMeasure) {
                this.drawColumnsItem(list);
            }
            const column = columns.find((c) => c.displayName === field.name);
            const isMeasure = !field.isCategory && !field.isComment && !field.isText;
            // a left-out text column: it can become a level, the group column, Comments or a text measure
            const isDimension = field.isText && field.excluded && !field.isCategory && !field.isComment && !field.isGroupBy;
            const lastMeasureInUse = isMeasure && !field.excluded && measuresInUse <= 1;
            const lastLevel = field.isCategory && levels.length <= 1;
            const item = list
                .append(DIV)
                .classed("item-vf pivot-field", true)
                .classed("dimension", field.isText && !field.isComment)
                .classed("measure", isMeasure)
                .classed("indented", isMeasure)
                .classed("comment", field.isComment)
                .classed("draggable", true);
            item.append("input")
                .attr("type", "checkbox")
                .property("checked", field.isGroupBy || field.isCategory || !field.excluded)
                .property("disabled", lastMeasureInUse || lastLevel)
                .on("change", () => {
                    const checked = (<HTMLInputElement>(<Event>d3.event).target).checked;
                    if (field.isCategory) {
                        // unticking a level takes it out of Category
                        void this.setCategoryColumns(levels.filter((level) => level !== field.name));
                    } else if (field.isGroupBy || isDimension) {
                        // ticking a text column groups the rows by it; unticking clears the group column
                        void this.setGroupByColumn(checked ? field.name : null);
                    } else {
                        void this.setColumnExcluded(field.name, !checked);
                    }
                });
            item.append("span").classed("label", true).text(field.name);
            // POC try-out: hovering a field selects its column in the sheet
            item.on(MOUSEENTER, () => this.hoverSourceColumn(field.name)).on(MOUSELEAVE, () => this.hoverSourceColumn(null));
            if (lastMeasureInUse) {
                item.attr("title", "At least one value column has to stay in use");
            }
            if (field.isCategory) {
                item.attr(
                    "title",
                    lastLevel ? "At least one field has to stay in Category" : `In Category: level ${levels.indexOf(field.name) + 1}`
                );
                if (!lastLevel) {
                    // a level can leave Category for Group, Comments or Actual straight from the list
                    const targets = [CategoricalRoles.Group, MeasureRoles.Comments, MeasureRoles.Values];
                    this.makeListItemDraggable(item, targets, (role) => void this.moveTextColumn(field.name, role, targets));
                } else {
                    item.classed("draggable", false);
                }
                return;
            }
            if (field.isGroupBy) {
                item.attr("title", "In Group: the rows are grouped by this column's values");
                const targets = [CategoricalRoles.Category, MeasureRoles.Comments, MeasureRoles.Values];
                this.makeListItemDraggable(item, targets, (role) => void this.moveTextColumn(field.name, role, targets));
                return;
            }
            if (isDimension) {
                item.attr(
                    "title",
                    "Text column; drop it on Category as a level, on Group to group the rows by its values, on Comments, or on Actual as a text measure"
                );
                const targets = [CategoricalRoles.Category, CategoricalRoles.Group, MeasureRoles.Comments, MeasureRoles.Values];
                this.makeListItemDraggable(item, targets, (role) => void this.moveTextColumn(field.name, role, targets));
                return;
            }
            if (field.excluded) {
                // a left-out comment column only goes back to Comments; a measure to any value bucket
                const targets: MeasureRoles[] = field.isComment ? [MeasureRoles.Comments] : Object.values(MeasureRoles);
                item.attr(
                    "title",
                    field.isComment
                        ? "Not used; tick it or drop it on Comments to include it"
                        : "Not used; tick it or drop it on a bucket to include it"
                );
                this.makeListItemDraggable(item, targets, (role) => void this.includeColumnWithRole(field.name, <MeasureRoles>role));
                return;
            }
            if (!column) {
                return;
            }
            const currentRole = <MeasureRoles>getColumnRole(column);
            if (field.isComment) {
                // a comment field belongs to Comments only
                item.classed("draggable", false);
                return;
            }
            // a text measure can become the group column or a level as well
            const targets: (MeasureRoles | CategoricalRoles)[] = Object.values(MeasureRoles);
            if (field.isText) {
                targets.push(CategoricalRoles.Group, CategoricalRoles.Category);
            }
            this.makeListItemDraggable(item, targets, (newRole) => {
                if (newRole === CategoricalRoles.Group) {
                    void this.setGroupByColumn(field.name);
                    return;
                }
                if (newRole === CategoricalRoles.Category) {
                    void this.setCategoryColumns([...levels, field.name]);
                    return;
                }
                if (newRole === currentRole) {
                    return;
                }
                const returnData = new Map<string, any>();
                this.applyMeasureRole(column, currentRole, <MeasureRoles>newRole, returnData);
                void this.dropFade().then(() => this.notify(returnData, `${this.getClassName()}Observer`));
            });
        });
    }

    /** POC try-out: the selection to put back once the cursor leaves the Fields list */
    private previousSelection: string = null;
    private hoverTimer: number = null;

    /** Excel: hovering a field selects its source column (debounced); null puts the previous selection back */
    private hoverSourceColumn(name: string | string[] | null) {
        if (Office.context.host !== Office.HostType.Excel) {
            return;
        }
        window.clearTimeout(this.hoverTimer);
        this.hoverTimer = window.setTimeout(() => void this.selectSourceColumn(name), 150);
    }

    /** Selects the source column(s) with these headers in the sheet; null restores the selection from before the hover */
    private async selectSourceColumn(name: string | string[] | null) {
        const dataSource: DataSource = getOfficeSettings(DATA_SOURCE);
        if (!dataSource?.range && !dataSource?.table) {
            return;
        }
        try {
            await Excel.run(async (context) => {
                const worksheet = context.workbook.worksheets.getActiveWorksheet();
                if (name === null) {
                    if (this.previousSelection) {
                        // "Sheet1!A1:A5, Sheet1!C1:C5": one sheet, one or more areas
                        const areas = this.previousSelection.split(",").map((area) => area.trim());
                        const sheet = areas[0].split("!", 2)[0].replace(/^'|'$/g, "");
                        const addresses = areas.map((area) => area.split("!", 2).pop());
                        context.workbook.worksheets.getItem(sheet).getRanges(addresses.join(",")).select();
                        this.previousSelection = null;
                        await context.sync();
                    }
                    return;
                }
                if (!this.previousSelection) {
                    // the selection before the first hover, read as areas: the hover itself may leave several selected
                    const selected = context.workbook.getSelectedRanges();
                    selected.load({ address: true });
                    await context.sync();
                    this.previousSelection = selected.address;
                }
                const range = dataSource.table
                    ? (await getExcelTable(context, worksheet, dataSource.table, dataSource.tableId)).getRange()
                    : worksheet.getRange(dataSource.range.toString().split("!", 2).pop());
                const header = range.getRow(0);
                header.load({ values: true });
                await context.sync();

                const names = Array.isArray(name) ? name : [name];
                const columns = header.values[0]
                    .map((v: unknown, index: number) => (names.includes(String(v ?? "").trim()) ? index : -1))
                    .filter((index: number) => index >= 0);
                if (columns.length === 0) {
                    return;
                }
                // adjacent columns form one area, so they get Excel's normal selection look; a gap starts a new area
                const runs: [number, number][] = [];
                columns.forEach((column: number) => {
                    const last = runs[runs.length - 1];
                    if (last && last[1] === column - 1) {
                        last[1] = column;
                    } else {
                        runs.push([column, column]);
                    }
                });
                const areas = runs.map(([from, to]) => range.getColumn(from).getBoundingRect(range.getColumn(to)));
                if (areas.length === 1) {
                    areas[0].select();
                    await context.sync();
                    return;
                }
                areas.forEach((area) => area.load({ address: true }));
                await context.sync();
                worksheet.getRanges(areas.map((area) => area.address.split("!", 2).pop()).join(",")).select();
                await context.sync();
            });
        } catch (error) {
            console.log("hover select failed", error);
        }
    }

    /** Measure columns in use (numeric, not Comments, not left out): the members of "Columns (n)" */
    private measuresInUse() {
        return (this.dataView?.sourceValueColumns ?? []).filter(
            (s) => !s.isCategory && !s.isGroupBy && !s.isComment && !s.isText && !s.excluded
        );
    }

    private columnsLabel(): string {
        return `Columns (${this.measuresInUse().length})`;
    }

    /**
     * The fictional "Columns (n)" list field: ticked, the measure columns in use are the groups (a cross table). Its
     * checkbox is that choice, not "tick all measures"; the measures below it keep their own in-use ticks.
     */
    private drawColumnsItem(list: d3.Selection<HTMLDivElement, any, any, any>) {
        const on = !!this.dataView?.columnsAsGroups;
        const item = list.append(DIV).classed("item-vf pivot-field columns draggable", true);
        item.append("input")
            .attr("type", "checkbox")
            .property("checked", on)
            .on("change", () => void this.setColumnsAsGroups((<HTMLInputElement>(<Event>d3.event).target).checked));
        item.append("span").classed("label", true).text(this.columnsLabel());
        item.attr("title", "Use the measure columns below as groups, one each: tick it or drop it on Group");
        const names = this.measuresInUse().map((s) => s.name);
        item.on(MOUSEENTER, () => this.hoverSourceColumn(names)).on(MOUSELEAVE, () => this.hoverSourceColumn(null));
        if (!on) {
            this.makeListItemDraggable(
                item,
                [CategoricalRoles.Group],
                () => void this.setColumnsAsGroups(true),
                FieldsTablesSwitcher.COLUMNS_FIELD
            );
        }
    }

    /** The "Columns (n)" bucket item of a cross table: × or a drag back to the list stops using the columns as groups */
    private renderColumnsItem(item: d3.Selection<HTMLDivElement, unknown, HTMLElement, unknown>) {
        item.attr("title", "The measure columns in use are the groups, one each");
        if (!this.columnFieldsEnabled) {
            return;
        }
        item.append("button")
            .classed("remove-field", true)
            .attr("title", "Stop using the columns as groups")
            .text("×")
            .on(CLICK, () => void this.setColumnsAsGroups(false));
        const draggable = d3
            .drag()
            .filter(() => !(<Element>(<Event>d3.event).target).closest(".remove-field"))
            .on(DRAGSTART, () => this.liftDraggedItem(item, [], true))
            .on(DRAG, () => {
                this.followCursor();
                this.highlightBucketUnderCursor([]);
            })
            .on(DRAGEND, () => {
                const droppedOnFieldsList = this.canDropOnFieldsList && this.isCursorOverFieldsList();
                this.dropDraggedItem(item);
                if (droppedOnFieldsList) {
                    void this.setColumnsAsGroups(false);
                }
            });
        item.classed("draggable", true).call(<any>draggable);
    }

    /** Uses the measure columns as groups (a cross table) or stops; excludes the group column */
    private async setColumnsAsGroups(on: boolean) {
        const settings = Visual.visualSettings;
        if (!settings || !!settings.showCrossTables === on) {
            return;
        }
        settings.showCrossTables = on;
        if (on) {
            settings.groupByColumn = null;
        }
        settings.persist();
        await this.rereadSource(settings);
    }

    /** Flat sources: the source column the user dropped on Group, if any */
    private getGroupBySourceColumn() {
        return this.dataView?.sourceValueColumns?.find((s) => s.isGroupBy) ?? null;
    }

    private isTextSourceColumn(column: DataViewMetadataColumn): boolean {
        return !!this.dataView?.sourceValueColumns?.some((s) => s.isText && s.name === column.displayName);
    }

    /** Pivots the rows by a text column's values; null clears it (a chooser cross table comes back when cleared) */
    private async setGroupByColumn(name: string | null) {
        const settings = Visual.visualSettings;
        if (!settings || (settings.groupByColumn ?? null) === name) {
            return;
        }
        if (name) {
            // a level, Comments or text measure dragged to Group leaves its old place; the group column replaces "Columns (n)"
            this.releaseTextColumn(settings, name);
            settings.showCrossTables = false;
        }
        settings.groupByColumn = name;
        settings.persist();
        await this.rereadSource(settings);
    }

    /** Makes a text column the Comments field (text columns are Comments only by header or by this choice) */
    private async setCommentColumn(name: string) {
        const settings = Visual.visualSettings;
        if (!settings || settings.commentColumn === name) {
            return;
        }
        this.releaseTextColumn(settings, name);
        settings.commentColumn = name;
        settings.persist();
        await this.rereadSource(settings);
    }

    /** Makes a text column a measure with the given value role (text measures are additional measures) */
    private async setTextMeasure(name: string, role: MeasureRoles) {
        const settings = Visual.visualSettings;
        if (!settings) {
            return;
        }
        this.releaseTextColumn(settings, name);
        settings.textMeasureColumns = [...(settings.textMeasureColumns ?? []).filter((n) => n !== name), name];
        settings.excludedColumns = (settings.excludedColumns ?? []).filter((n) => n !== name);
        settings.persist();
        await this.rereadSource(settings);
        await this.applyRoleAfterReread(name, role);
    }

    /** Takes a text column out of whatever it was before (level, group column, Comments, text measure) */
    private releaseTextColumn(settings: VarianceSettings, name: string) {
        if (settings.groupByColumn === name) {
            settings.groupByColumn = null;
        }
        if (settings.commentColumn === name) {
            settings.commentColumn = null;
        }
        const levels = this.getCategoryLevels();
        if (levels.includes(name)) {
            settings.categoryColumns = levels.filter((level) => level !== name);
        }
        settings.textMeasureColumns = (settings.textMeasureColumns ?? []).filter((n) => n !== name);
    }

    /** Routes a text column dropped on a bucket to the right setter; `allowed` keeps stray drops out */
    private async moveTextColumn(name: string, role: MeasureRoles | CategoricalRoles, allowed: (MeasureRoles | CategoricalRoles)[]) {
        if (!role || !allowed.includes(role)) {
            return;
        }
        if (role === CategoricalRoles.Category) {
            await this.setCategoryColumns([...this.getCategoryLevels(), name]);
        } else if (role === CategoricalRoles.Group) {
            await this.setGroupByColumn(name);
        } else if (role === MeasureRoles.Comments) {
            await this.setCommentColumn(name);
        } else {
            await this.setTextMeasure(name, role);
        }
    }

    /** After a re-read the column has an auto-assigned role; switch it to the one the user dropped it on */
    private async applyRoleAfterReread(name: string, role: MeasureRoles) {
        const visual = Visual.getInstance();
        const columns = visual.dataView?.metadata?.columns ?? [];
        const column = columns.find((c) => c.index !== undefined && c.displayName === name);
        const settings = Visual.visualSettings;
        if (!column || !settings?.measureRoles) {
            return;
        }
        const currentRole = <MeasureRoles>getColumnRole(column);
        if (currentRole === role) {
            return;
        }
        if (this.shouldSwitchRoles(role, columns)) {
            const existingColumn = columns.find((c) => c.roles[role] && c.index !== column.index);
            if (existingColumn) {
                settings.measureRoles[existingColumn.index] = currentRole;
                existingColumn.roles = { [currentRole]: true };
            }
        }
        settings.measureRoles[column.index] = role;
        column.roles = { [role]: true };
        settings.persist();
        await visual.constructViewModelAndUpdate(settings);
    }

    /** Length of the drop-target fade in ui.less; the pane is rebuilt only after it has played */
    private static readonly DROP_FADE_MS = 120;

    private dropFade(): Promise<void> {
        return new Promise((resolve) => window.setTimeout(resolve, FieldsTablesSwitcher.DROP_FADE_MS));
    }

    /** Excel re-reads the range / table; PowerPoint re-parses the data-editor sheet. Waits for the drop fade first. */
    private async rereadSource(settings: VarianceSettings) {
        await this.dropFade();
        const visual = Visual.getInstance();
        if (Office.context.host === Office.HostType.PowerPoint) {
            await visual.reparseEditorData(settings);
        } else {
            await visual.update(settings);
        }
    }

    /** Leaves a source column out of the visual (or brings it back); the visual re-reads the source */
    private async setColumnExcluded(name: string, excluded: boolean) {
        const settings = Visual.visualSettings;
        if (!settings) {
            return;
        }
        const current = new Set(settings.excludedColumns ?? []);
        if (excluded) {
            current.add(name);
        } else {
            current.delete(name);
        }
        settings.excludedColumns = [...current];
        settings.persist();
        await this.rereadSource(settings);
    }

    /**
     * Brings a left-out column back and gives it the role of the bucket it was dropped on. The re-read auto-assigns a
     * role first; the switch only happens when that differs from the requested one.
     */
    private async includeColumnWithRole(name: string, role: MeasureRoles) {
        await this.setColumnExcluded(name, false);
        await this.applyRoleAfterReread(name, role);
    }

    /** Removes a bucket item: a level leaves Category, a measure is left out of the source read */
    private removeBucketField(dataColumn: DataViewMetadataColumn) {
        if (this.columnFieldsEnabled && dataColumn.roles[CategoricalRoles.Category]) {
            void this.setCategoryColumns(this.getCategoryLevels().filter((level) => level !== dataColumn.displayName));
        } else if (this.columnFieldsEnabled) {
            void this.setColumnExcluded(dataColumn.displayName, true);
        }
    }

    /** Flat sources: only real source columns can be removed, not the synthetic "Values" of a cross table */
    private canRemoveBucketField(dataColumn: DataViewMetadataColumn, currentRole: MeasureRoles | CategoricalRoles): boolean {
        if (this.isLastFieldInArea(currentRole)) {
            return false;
        }
        return (
            this.columnFieldsEnabled
            && (currentRole in MeasureRoles || currentRole === CategoricalRoles.Category)
            && !!this.dataView?.sourceValueColumns?.some((s) => s.name === dataColumn.displayName)
        );
    }

    /** Flat sources: the Category levels by header, in order */
    private getCategoryLevels(): string[] {
        return (this.dataView?.metadata?.columns ?? []).filter((c) => c.roles[CategoricalRoles.Category]).map((c) => c.displayName);
    }

    /** Sets the Category levels; a level can't be the group column or Comments at the same time */
    private async setCategoryColumns(levels: string[]) {
        const settings = Visual.visualSettings;
        if (!settings || levels.length === 0) {
            return;
        }
        settings.categoryColumns = levels;
        if (levels.includes(settings.groupByColumn)) {
            settings.groupByColumn = null;
        }
        if (levels.includes(settings.commentColumn)) {
            settings.commentColumn = null;
        }
        settings.textMeasureColumns = (settings.textMeasureColumns ?? []).filter((n) => !levels.includes(n));
        settings.persist();
        await this.rereadSource(settings);
    }

    /** Moves a level to `index` among the other levels (the slot the insertion marker pointed at) */
    private async moveCategoryLevel(name: string, index: number) {
        const levels = this.getCategoryLevels().filter((level) => level !== name);
        levels.splice(Math.max(0, Math.min(index, levels.length)), 0, name);
        await this.setCategoryColumns(levels);
    }

    private makeListItemDraggable(
        item: d3.Selection<HTMLDivElement, any, any, any>,
        dropTargets: (MeasureRoles | CategoricalRoles)[],
        onDrop: (role: MeasureRoles | CategoricalRoles) => void,
        draggedName: string = null
    ) {
        // full buckets are left out at drag start, so they never highlight
        let targets: (MeasureRoles | CategoricalRoles)[] = [];
        const draggable = d3
            .drag()
            .filter(() => !(d3.event.target instanceof HTMLInputElement))
            .on(DRAGSTART, () => {
                targets = this.openTargets(dropTargets, draggedName);
                this.liftDraggedItem(item, targets);
            })
            .on(DRAG, () => {
                this.followCursor();
                this.highlightBucketUnderCursor(targets);
            })
            .on(DRAGEND, () => {
                this.dropDraggedItem(item);
                const role = this.findRoleUnderCursor(d3.event);
                if (role && targets.includes(role)) {
                    this.placeInBucket(item, item.select(".label").text(), role);
                    onDrop(role);
                }
            });
        draggable(item);
    }

    /**
     * The item itself stays in its bucket (dimmed) so nothing reflows; a translucent copy follows the cursor.
     */
    private liftDraggedItem(
        item: d3.Selection<HTMLDivElement, any, any, any>,
        dropTargets: (MeasureRoles | CategoricalRoles)[],
        /** dropping on the Fields list removes the field from its bucket */
        canDropOnFieldsList = false
    ) {
        this.canDropOnFieldsList = canDropOnFieldsList;
        this.fieldsList?.classed("drop-target", canDropOnFieldsList);
        const ghostNode = <HTMLDivElement>item.node().cloneNode(true);
        ghostNode.querySelector(".remove-field")?.remove();
        this.dragGhost = d3
            .select(ghostNode)
            .classed("dragging", true)
            .style("width", item.node().getBoundingClientRect().width + PX);
        this.innerForm.node().appendChild(ghostNode);
        // keep the point where the item was grabbed under the cursor, so it doesn't jump on mousedown
        const [x, y] = d3.mouse(this.innerForm.node());
        const itemRect = item.node().getBoundingClientRect();
        const formRect = this.innerForm.node().getBoundingClientRect();
        this.dragGrabOffset = { x: x - (itemRect.left - formRect.left), y: y - (itemRect.top - formRect.top) };
        item.classed("drag-origin", true);
        this.innerForm.classed("drag-in-progress", true);
        this.followCursor();
        dropTargets.forEach((role) => this.getBucketForRole(role)?.classed("drop-target", true));
    }

    /** Buckets a field may be dropped into, given where it currently sits */
    /**
     * Shows a drop right away instead of after the re-read: the field's bucket item moves into the target bucket (a
     * list item moves the field's existing bucket item, or shows a copy of itself); the re-read then redraws for real.
     */
    private placeInBucket(
        item: d3.Selection<HTMLDivElement, any, any, any>,
        name: string,
        role: MeasureRoles | CategoricalRoles,
        index: number = null
    ) {
        const bucket = this.getBucketForRole(role)?.node();
        if (!bucket) {
            return;
        }
        let node = item.node();
        if (!node.closest(".bucket")) {
            const existing = Array.from(this.innerForm.node().querySelectorAll<HTMLDivElement>(".bucket .item-vf")).find(
                (n) => n.querySelector(".label")?.textContent === name
            );
            if (existing) {
                node = existing;
            } else {
                node = <HTMLDivElement>node.cloneNode(true);
                node.querySelector("input")?.remove();
                node.querySelector(".remove-field")?.remove();
                node.classList.remove("pivot-field", "dimension", "measure", "comment", "draggable");
            }
        }
        node.classList.remove("drag-origin");
        const others = Array.from(bucket.querySelectorAll<HTMLDivElement>(".item-vf")).filter((n) => n !== node);
        if (index !== null && index < others.length) {
            bucket.insertBefore(node, others[index]);
        } else {
            bucket.appendChild(node);
        }
    }

    /** A bucket at capacity neither highlights nor accepts a drop; the dragged field's own bucket doesn't count it */
    private isBucketFull(role: MeasureRoles | CategoricalRoles, draggedName: string = null): boolean {
        const columns = (this.dataView?.metadata?.columns ?? []).filter((c) => c.displayName !== draggedName);
        if (role === CategoricalRoles.Category) {
            return false;
        }
        if (role === CategoricalRoles.Group) {
            if (this.dataView?.columnsAsGroups || draggedName === FieldsTablesSwitcher.COLUMNS_FIELD) {
                return false;
            }
            return columns.some((c) => c.roles[CategoricalRoles.Group]);
        }
        return columns.filter((c) => c.index !== undefined && c.roles[role]).length >= getMaxRoleCount(<MeasureRoles>role);
    }

    private openTargets(targets: (MeasureRoles | CategoricalRoles)[], draggedName: string = null): (MeasureRoles | CategoricalRoles)[] {
        return targets.filter((role) => !this.isBucketFull(role, draggedName));
    }

    private getDropTargets(
        currentRole: MeasureRoles | CategoricalRoles,
        column: DataViewMetadataColumn = null
    ): (MeasureRoles | CategoricalRoles)[] {
        return this.openTargets(this.getAllowedTargets(currentRole, column), column?.displayName ?? null);
    }

    private getAllowedTargets(
        currentRole: MeasureRoles | CategoricalRoles,
        column: DataViewMetadataColumn = null
    ): (MeasureRoles | CategoricalRoles)[] {
        const lastInArea = this.isLastFieldInArea(currentRole);
        if (currentRole === MeasureRoles.Comments) {
            // a comment field belongs to Comments only; it can be removed (×, drag to the list) but not moved
            return [];
        }
        if (this.columnFieldsEnabled && currentRole === CategoricalRoles.Category) {
            // flat sources: a level reorders among the levels, becomes the group column, Comments or a text measure; the last level stays
            return lastInArea ? [] : [CategoricalRoles.Category, CategoricalRoles.Group, MeasureRoles.Comments, MeasureRoles.Values];
        }
        // measures switch roles among the value buckets (dropping on the own bucket reorders PL/FC)
        const targets: (MeasureRoles | CategoricalRoles)[] = currentRole in MeasureRoles ? Object.values(MeasureRoles) : [];
        if (this.columnFieldsEnabled && column && this.isTextSourceColumn(column)) {
            // a text column in a value bucket (Comments) can become the group column or a Category level
            targets.push(CategoricalRoles.Group, CategoricalRoles.Category);
        }
        return targets;
    }

    /**
     * Slot under the cursor among the other items of a bucket, in display order; null when the drop position is unchanged.
     */
    /** Where the dragged item would land among the other items of the bucket, or null when the cursor is outside it */
    private getBucketSlotUnderCursor(
        bucket: d3.Selection<HTMLDivElement, any, any, any>,
        dragged: d3.Selection<HTMLDivElement, any, any, any>
    ): { index: number; currentIndex: number; others: HTMLDivElement[] } {
        const cursor = d3.event?.sourceEvent;
        if (!cursor) {
            return null;
        }
        const bucketRect = bucket.node().getBoundingClientRect();
        const inside =
            cursor.clientX >= bucketRect.left
            && cursor.clientX <= bucketRect.right
            && cursor.clientY >= bucketRect.top
            && cursor.clientY <= bucketRect.bottom;
        const items = Array.from(bucket.node().querySelectorAll<HTMLDivElement>(".item-vf"));
        const others = items.filter((node) => node !== dragged.node());
        if (!inside || others.length === 0) {
            return null;
        }
        const index = others.filter((node) => {
            const rect = node.getBoundingClientRect();
            return cursor.clientY > rect.top + rect.height / 2;
        }).length;
        return { index, currentIndex: items.indexOf(dragged.node()), others };
    }

    /** Draws the insertion line at the slot the dragged item would take */
    private updateInsertionMarker(
        bucket: d3.Selection<HTMLDivElement, any, any, any>,
        dragged: d3.Selection<HTMLDivElement, any, any, any>
    ) {
        this.clearInsertionMarker();
        const slot = this.getBucketSlotUnderCursor(bucket, dragged);
        if (!slot || slot.index === slot.currentIndex) {
            return;
        }
        this.pendingInsertIndex = slot.index;
        if (slot.index < slot.others.length) {
            slot.others[slot.index].classList.add("insert-before");
        } else {
            slot.others[slot.others.length - 1].classList.add("insert-after");
        }
    }

    /** Marks the allowed bucket under the cursor as the one the dragged field will land in */
    private highlightBucketUnderCursor(dropTargets: (MeasureRoles | CategoricalRoles)[]) {
        const role = this.findRoleUnderCursor(d3.event);
        const active = role && dropTargets.includes(role) ? this.getBucketForRole(role)?.node() : null;
        d3.selectAll<HTMLDivElement, any>(".bucket.drop-target").classed("drop-active", function () {
            return this === active;
        });
        this.fieldsList?.classed("drop-active", this.canDropOnFieldsList && this.isCursorOverFieldsList());
    }

    private isCursorOverFieldsList(): boolean {
        const cursor = d3.event?.sourceEvent;
        const list = this.fieldsList?.node();
        if (!cursor || !list) {
            return false;
        }
        const rect = list.getBoundingClientRect();
        return cursor.clientX >= rect.left && cursor.clientX <= rect.right && cursor.clientY >= rect.top && cursor.clientY <= rect.bottom;
    }

    private clearInsertionMarker() {
        this.pendingInsertIndex = null;
        d3.selectAll(".item-vf.insert-before, .item-vf.insert-after").classed("insert-before insert-after", false);
    }

    /**
     * The parser needs at least one row and one value field, so the last field in either area can't leave it.
     */
    private isLastFieldInArea(role: MeasureRoles | CategoricalRoles): boolean {
        const columns = this.dataView?.metadata?.columns ?? [];
        if (role === CategoricalRoles.Category) {
            return columns.filter((c) => c.roles[CategoricalRoles.Category]).length <= 1;
        }
        if (role in MeasureRoles) {
            return columns.filter((c) => c.index !== undefined).length <= 1;
        }
        return false;
    }

    private getLockedFieldHint(role: MeasureRoles | CategoricalRoles): string {
        return role === CategoricalRoles.Category
            ? "At least one field has to stay in Category"
            : "At least one field has to stay in the value buckets";
    }

    private followCursor() {
        if (!this.dragGhost) {
            return;
        }
        const [x, y] = d3.mouse(this.innerForm.node());
        this.dragGhost.style("left", x - this.dragGrabOffset.x + PX).style("top", y - this.dragGrabOffset.y + PX);
    }

    private dropDraggedItem(item: d3.Selection<HTMLDivElement, any, any, any>) {
        this.dragGhost?.remove();
        this.dragGhost = null;
        item.classed("drag-origin", false);
        this.innerForm.classed("drag-in-progress", false);
        d3.selectAll(".bucket.drop-target").classed("drop-target drop-active", false);
        this.fieldsList?.classed("drop-target drop-active", false);
        this.canDropOnFieldsList = false;
        this.clearInsertionMarker();
    }

    /** The group column in Group; × or dragging it back to the Fields list clears it, dropping it on Category makes it a level */
    private renderGroupByItem(item: d3.Selection<HTMLDivElement, unknown, HTMLElement, unknown>) {
        item.attr("title", "The rows are grouped by this column's values");
        item.append("button")
            .classed("remove-field", true)
            .attr("title", "Remove the group column")
            .text("×")
            .on(CLICK, () => void this.setGroupByColumn(null));

        const groupName = (<DataViewMetadataColumn>item.datum()).displayName;
        const dropTargets = this.openTargets([CategoricalRoles.Category, MeasureRoles.Values, MeasureRoles.Comments], groupName);
        const draggable = d3
            .drag()
            .filter(() => !(<Element>(<Event>d3.event).target).closest(".remove-field"))
            .on(DRAGSTART, () => this.liftDraggedItem(item, dropTargets, true))
            .on(DRAG, () => {
                this.followCursor();
                this.highlightBucketUnderCursor(dropTargets);
            })
            .on(DRAGEND, () => {
                const droppedOnFieldsList = this.canDropOnFieldsList && this.isCursorOverFieldsList();
                this.dropDraggedItem(item);
                const name = (<DataViewMetadataColumn>item.datum()).displayName;
                if (droppedOnFieldsList) {
                    void this.setGroupByColumn(null);
                    return;
                }
                const role = this.findRoleUnderCursor(d3.event);
                if (role && dropTargets.includes(role)) {
                    this.placeInBucket(item, name, role);
                }
                void this.moveTextColumn(name, role, dropTargets);
            });
        item.classed("draggable", true);
        draggable(item);
    }

    private getPositionWithinBucket(top: number, role: MeasureRoles): number {
        if (role === MeasureRoles.Plan) {
            const startPos = this.bucketPL.node().offsetTop;
            const diff = top - startPos;
            if (diff < 12) {
                return 0;
            } else if (diff < 28) {
                return 1;
            } else {
                //if (diff < 72) {
                return 2;
            }
        } else if (role === MeasureRoles.Forecast) {
            const startPos = this.bucketFC.node().offsetTop; //getBoundingClientRect().top;
            const diff = top - startPos;
            if (diff < 12) {
                return 0;
            } else if (diff < 28) {
                return 1;
            } else {
                //if (diff < 72) {
                return 2;
            }
        }

        return 0;
    }

    /** Gives a value column a new role; single-slot roles (PY, Comments, full Plan / Forecast) hand their old column the vacated role */
    private applyMeasureRole(
        dataColumn: DataViewMetadataColumn,
        currentRole: MeasureRoles,
        newRole: MeasureRoles,
        returnData: Map<string, any>
    ) {
        // Don't switch roles for "Values" role, allow multiple values in bucket
        if (this.shouldSwitchRoles(newRole, this.dataView.metadata.columns)) {
            const existingColumn = this.dataView.metadata.columns.find((column) => column.roles[newRole]);
            if (existingColumn) {
                this.setNewMeasureRoleSettings(currentRole, existingColumn.index, returnData);
                existingColumn.roles = { [currentRole]: true };
            }
        }

        this.setNewMeasureRoleSettings(newRole, dataColumn.index, returnData);
        const columnToSwitch = this.dataView.metadata.columns.find((valsrc) => valsrc.index === dataColumn.index);
        columnToSwitch.roles = { [newRole]: true };
    }

    private shouldSwitchRoles(newRole: MeasureRoles, columns: DataViewMetadataColumn[]): boolean {
        return (
            newRole === MeasureRoles.PreviousYear
            || newRole === MeasureRoles.Comments
            || (newRole === MeasureRoles.Plan && columns.filter((column) => column.roles[MeasureRoles.Plan]).length > 2)
            || (newRole === MeasureRoles.Forecast && columns.filter((column) => column.roles[MeasureRoles.Forecast]).length > 2)
        );
    }

    public setNewMeasureRoleSettings(role: MeasureRoles, index: number, returnData: Map<string, any>) {
        if (index < this.settings.measureRoles.length) {
            this.settings.measureRoles[index] = role;
            returnData.set("measureRoles", this.settings.measureRoles);
        }
    }

    private createFieldsForm(innerForm: d3.Selection<HTMLDivElement, any, any, any>) {
        this.innerForm = innerForm;
        const dataSource: DataSource = getOfficeSettings(DATA_SOURCE);
        // flat sources: Excel ranges / tables and the PowerPoint data editor (pivot sources show the buckets only)
        this.columnFieldsEnabled =
            Office.context.host === Office.HostType.PowerPoint
            || (Office.context.host === Office.HostType.Excel && !!(dataSource?.range || dataSource?.table));
        this.drawColumnFieldsList(innerForm);

        innerForm.append(P).text("Category");
        this.bucketCategory = innerForm.append(DIV).classed("bucket category", true);
        innerForm.append(P).text("Group");
        this.bucketGroup = innerForm.append(DIV).classed("bucket group", true);

        innerForm.append(P).text("Actual");
        this.bucketActual = innerForm.append(DIV).classed("bucket actual", true);
        innerForm.append(P).text("Previous Year");
        this.bucketPY = innerForm.append(DIV).classed("bucket py", true);
        innerForm.append(P).text("Plan");
        this.bucketPL = innerForm.append(DIV).classed("bucket pl", true);
        innerForm.append(P).text("Forecast");
        this.bucketFC = innerForm.append(DIV).classed("bucket fc", true);

        innerForm.append(P).text("Comments");
        this.bucketComment = innerForm.append(DIV).classed("bucket comment", true);

        this.drawValueFieldsItems(innerForm);

        if (Office.context.host === Office.HostType.Excel) {
            this.createExcelEditDataSourceUI(innerForm);
        }
    }

    private createExcelEditDataSourceUI(innerForm: d3.Selection<HTMLDivElement, any, any, any>) {
        const formParent = innerForm.node().parentElement;
        const editDataDiv = document.createElement("div");
        formParent.insertBefore(editDataDiv, innerForm.node());
        editDataDiv.classList.add("edit-source-data");
        const editDataDivSelection = d3.select(editDataDiv);

        const dataSource = getOfficeSettings(DATA_SOURCE);
        const dataSourceDisplayString = this.getDataSourceDisplayString(dataSource);

        editDataDivSelection.append(P).classed("data-source-label", true).text("Data source");
        const editDataSourceDiv = editDataDivSelection.append(DIV);
        editDataSourceDiv.append(P).classed("data-source", true).text(dataSourceDisplayString);

        if (dataSource) {
            const editDatBtn = editDataSourceDiv.append("button").classed("edit-data-source-btn", true);

            const dataEditHandler = () => {
                Visual.getInstance().update(Visual.visualSettings);
            };
            editDatBtn.on(CLICK, () => {
                if (d3?.event?.detail > 1) {
                    // prevent double click
                    return;
                }
                createDialog(dataSource, dataEditHandler);
            });
        }
    }

    private getDataSourceDisplayString(dataSource: DataSource): string {
        if (!dataSource) {
            return "";
        } else if (dataSource.range) {
            return "Range: " + dataSource.range;
        } else if (dataSource.table) {
            return "Excel table: " + dataSource.table;
        } else if (dataSource.pivotTable) {
            return "Pivot table: " + dataSource.pivotTable;
        }
    }

    private getBucketForColumnRole(column: DataViewMetadataColumn): d3.Selection<HTMLDivElement, any, any, any> {
        if (column.roles[MeasureRoles.Values]) {
            return this.bucketActual;
        } else if (column.roles[MeasureRoles.PreviousYear]) {
            return this.bucketPY;
        } else if (column.roles[MeasureRoles.Plan]) {
            return this.bucketPL;
        } else if (column.roles[MeasureRoles.Forecast]) {
            return this.bucketFC;
        } else if (column.roles[MeasureRoles.Comments]) {
            return this.bucketComment;
        } else if (column.roles[CategoricalRoles.Category]) {
            return this.bucketCategory;
        } else if (column.roles[CategoricalRoles.Group]) {
            return this.bucketGroup;
        } else {
            return null;
        }
    }

    private getBucketForRole(role: MeasureRoles | CategoricalRoles): d3.Selection<HTMLDivElement, any, any, any> {
        switch (role) {
            case MeasureRoles.Values:
                return this.bucketActual;
            case MeasureRoles.PreviousYear:
                return this.bucketPY;
            case MeasureRoles.Plan:
                return this.bucketPL;
            case MeasureRoles.Forecast:
                return this.bucketFC;
            case MeasureRoles.Comments:
                return this.bucketComment;
            case CategoricalRoles.Category:
                return this.bucketCategory;
            case CategoricalRoles.Group:
                return this.bucketGroup;
            default:
                return null;
        }
    }

    /** The bucket under the cursor; the label above a bucket and the items inside it count as part of it */
    private findRoleUnderCursor(d3Event): MeasureRoles | CategoricalRoles {
        const cursor = d3Event?.sourceEvent;
        if (!cursor) {
            return null;
        }
        const buckets: [d3.Selection<HTMLDivElement, any, any, any>, MeasureRoles | CategoricalRoles][] = [
            [this.bucketCategory, CategoricalRoles.Category],
            [this.bucketGroup, CategoricalRoles.Group],
            [this.bucketActual, MeasureRoles.Values],
            [this.bucketPY, MeasureRoles.PreviousYear],
            [this.bucketPL, MeasureRoles.Plan],
            [this.bucketFC, MeasureRoles.Forecast],
            [this.bucketComment, MeasureRoles.Comments],
        ];
        for (const [bucket, role] of buckets) {
            const node = bucket?.node();
            if (!node) {
                continue;
            }
            const rect = node.getBoundingClientRect();
            const label = node.previousElementSibling;
            const top = label?.tagName === "P" ? label.getBoundingClientRect().top : rect.top;
            if (cursor.clientX >= rect.left && cursor.clientX <= rect.right && cursor.clientY >= top && cursor.clientY <= rect.bottom) {
                return role;
            }
        }
        return null;
    }
}

export default FieldsTablesSwitcher;
